import {
  Grid,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  TableContainer,
  CircularProgress,
} from '@mui/material';
import HeaderAndSidebar from 'components/templates/HeaderAndSidebar';
import {
  convertToIncomeKindDisplayString,
  convertToOutgoKindDisplayString,
  Income,
  IncomeKind,
  Outgo,
  OutgoKind,
} from 'types/index';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import groupBy, { unixTimeToString } from 'libs';
import { useState } from 'react';
import OutgoFormModal from 'components/molecules/OutgoFormModal';
import IncomeFormModal from 'components/molecules/IncomeFormModal';

ChartJS.register(ArcElement, Tooltip, Legend);

const calculateInitialRegisteredAt = (year: number, month: number): number => {
  const currentDate = new Date();
  if (
    currentDate.getFullYear() === year &&
    currentDate.getMonth() + 1 === month
  ) {
    return Math.floor(new Date().getTime() / 1000);
  }

  return Math.floor(new Date(year, month, 0).getTime() / 1000);
};

type Props = {
  year: number;
  month: number;
  incomes: Income[];
  outgos: Outgo[];
  currentMonthTotalAsset: number;
  previousMonthTotalAsset: number;
  isLoading: boolean;
  onClickPreviousMonth: () => void;
  onClickNextMonth: () => void;
  onClickImportedOutgo: () => void;
  onClickDeleteOutgo: (outgo: Outgo) => void;
  onClickDeleteIncome: (income: Income) => void;
  onSubmitClickCreateOutgo: (outgo: Outgo) => void;
  onSubmitClickUpdateOutgo: (outgo: Outgo) => void;
  onSubmitClickCreateIncome: (income: Income) => void;
  onSubmitClickUpdateIncome: (income: Income) => void;
};

const MonthlyBalance: React.FC<Props> = ({
  year,
  month,
  incomes,
  outgos,
  currentMonthTotalAsset,
  previousMonthTotalAsset,
  isLoading,
  onClickPreviousMonth,
  onClickNextMonth,
  onClickImportedOutgo,
  onClickDeleteOutgo,
  onClickDeleteIncome,
  onSubmitClickCreateOutgo,
  onSubmitClickUpdateOutgo,
  onSubmitClickCreateIncome,
  onSubmitClickUpdateIncome,
}) => {
  const totalIncome = incomes
    .map((income) => income.amount)
    .reduce((sum, current) => sum + current, 0);
  const totalOutgo = outgos
    .map((outgo) => outgo.amount)
    .reduce((sum, current) => sum + current, 0);
  const calculatedCurrentMonthTotalAsset =
    previousMonthTotalAsset + totalIncome - totalOutgo;
  const errorAmount = currentMonthTotalAsset - calculatedCurrentMonthTotalAsset;
  const balanceAmount = totalIncome - totalOutgo;

  const groupedOutgos = groupBy(outgos, 'kind');
  const chartData = Object.keys(groupedOutgos)
    .map((key) => ({
      kind: key as OutgoKind,
      amount: groupedOutgos[key]
        .map((outgo) => outgo.amount)
        .reduce((sum, current) => sum + current, 0),
    }))
    .sort((a, b) => b.amount - a.amount);

  const [selectedOutgo, setSelectedOutgo] = useState<Outgo | null>(null);
  const [selectedIncome, setSelectedIncome] = useState<Income | null>(null);

  const [outgoModalOpen, setOutgoModalOpen] = useState(false);
  const onClickCreateOutgo = () => {
    setOutgoModalOpen(true);
    setSelectedOutgo(null);
  };

  const onClickSubmitOutgo = (
    id: number,
    name: string,
    kind: OutgoKind,
    amount: number,
    registeredAt: number,
  ) => {
    const outgo: Outgo = {
      id,
      name,
      kind,
      amount,
      registered_at: registeredAt,
    };
    if (id === 0) {
      onSubmitClickCreateOutgo(outgo);
    } else {
      onSubmitClickUpdateOutgo(outgo);
    }
    setOutgoModalOpen(false);
  };

  const [incomeModalOpen, setIncomeModalOpen] = useState(false);
  const onClickCreateIncome = () => {
    setIncomeModalOpen(true);
    setSelectedIncome(null);
  };

  const onClickSubmitIncome = (
    id: number,
    name: string,
    kind: IncomeKind,
    amount: number,
    registeredAt: number,
  ) => {
    const income: Income = {
      id,
      name,
      kind,
      amount,
      registered_at: registeredAt,
    };

    if (id === 0) {
      onSubmitClickCreateIncome(income);
    } else {
      onSubmitClickUpdateIncome(income);
    }
    setIncomeModalOpen(false);
  };

  const openOutgoUpdateForm = (outgo: Outgo) => {
    setSelectedOutgo(outgo);
    setOutgoModalOpen(true);
  };

  const openIncomeUpdateForm = (income: Income) => {
    setSelectedIncome(income);
    setIncomeModalOpen(true);
  };

  return (
    <HeaderAndSidebar pageTitle="月別収支">
      <Grid item xs={12} md={12} lg={12}>
        <Typography component="h2" variant="h4" color="inherit">
          {`${year}/${month.toString().padStart(2, '0')}`}
        </Typography>
        <Grid container spacing={1}>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={() => onClickPreviousMonth()}
            >
              <KeyboardArrowLeftIcon />
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={() => onClickNextMonth()}
            >
              <KeyboardArrowRightIcon />
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={6} md={6} lg={6}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography component="h2" variant="h6" color="primary" gutterBottom>
            支出
          </Typography>
          <Grid item>
            <Button variant="contained" onClick={() => onClickCreateOutgo()}>
              追加
            </Button>
            <Button variant="contained" onClick={onClickImportedOutgo}>
              インポート
            </Button>
          </Grid>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <TableContainer sx={{ maxHeight: 450 }}>
              <Table
                stickyHeader
                aria-label="sticky table"
                sx={{ maxHeight: 440 }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>登録日</TableCell>
                    <TableCell>名前</TableCell>
                    <TableCell>種別</TableCell>
                    <TableCell>金額</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {outgos.map((outgo) => (
                    <TableRow key={outgo.id}>
                      <TableCell>
                        {unixTimeToString(outgo.registered_at)}
                      </TableCell>
                      <TableCell>{outgo.name}</TableCell>
                      <TableCell>
                        {convertToOutgoKindDisplayString(outgo.kind)}
                      </TableCell>
                      <TableCell>
                        {outgo.amount.toLocaleString('ja-JP')}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          onClick={() => openOutgoUpdateForm(outgo)}
                        >
                          編集
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() => onClickDeleteOutgo(outgo)}
                        >
                          削除
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>
                      <Typography style={{ fontWeight: 600 }}>
                        支出合計
                      </Typography>
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell>{totalOutgo}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Grid>
      <Grid item xs={6} md={6} lg={6}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography component="h2" variant="h6" color="primary" gutterBottom>
            収入
          </Typography>
          <Grid item>
            <Button variant="contained" onClick={() => onClickCreateIncome()}>
              追加
            </Button>
          </Grid>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <TableContainer>
              <Table stickyHeader sx={{ maxHeight: 440 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>登録日</TableCell>
                    <TableCell>名前</TableCell>
                    <TableCell>種別</TableCell>
                    <TableCell>金額</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {incomes.map((income) => (
                    <TableRow key={income.id}>
                      <TableCell>
                        {unixTimeToString(income.registered_at)}
                      </TableCell>
                      <TableCell>{income.name}</TableCell>
                      <TableCell>
                        {convertToIncomeKindDisplayString(income.kind)}
                      </TableCell>
                      <TableCell>
                        {income.amount.toLocaleString('ja-JP')}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          onClick={() => openIncomeUpdateForm(income)}
                        >
                          編集
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() => onClickDeleteIncome(income)}
                        >
                          削除
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>
                      <Typography style={{ fontWeight: 600 }}>
                        収入合計
                      </Typography>
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell>{totalIncome}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Grid>

      <Grid item container alignItems="center" justifyContent="center">
        <Grid item xs={10} md={10} lg={10}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography
              component="h2"
              variant="h6"
              color="primary"
              gutterBottom
            >
              月別収支
            </Typography>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <Table sx={{ maxHeight: 440 }}>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography style={{ fontWeight: 600 }}>
                        先月合計金額
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {previousMonthTotalAsset.toLocaleString('ja-JP')}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography style={{ fontWeight: 600 }}>
                        計算合計金額
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {calculatedCurrentMonthTotalAsset.toLocaleString('ja-JP')}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography style={{ fontWeight: 600 }}>
                        合計金額
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {currentMonthTotalAsset.toLocaleString('ja-JP')}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography style={{ fontWeight: 600 }}>誤差</Typography>
                    </TableCell>
                    <TableCell>{errorAmount.toLocaleString('ja-JP')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography style={{ fontWeight: 600 }}>収支</Typography>
                    </TableCell>
                    <TableCell>
                      {balanceAmount.toLocaleString('ja-JP')}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Grid item xs={4} md={4} lg={4}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography component="h2" variant="h6" color="primary" gutterBottom>
            支出
          </Typography>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Pie
              data={{
                labels: chartData.map((data) =>
                  convertToOutgoKindDisplayString(data.kind),
                ),
                datasets: [
                  {
                    data: chartData.map((data) => data.amount),
                    backgroundColor: [
                      'rgba(255, 99, 132, 0.2)',
                      'rgba(54, 162, 235, 0.2)',
                      'rgba(255, 206, 86, 0.2)',
                      'rgba(75, 192, 192, 0.2)',
                      'rgba(153, 102, 255, 0.2)',
                      'rgba(255, 159, 64, 0.2)',
                    ],
                    borderColor: [
                      'rgba(255, 99, 132, 1)',
                      'rgba(54, 162, 235, 1)',
                      'rgba(255, 206, 86, 1)',
                      'rgba(75, 192, 192, 1)',
                      'rgba(153, 102, 255, 1)',
                      'rgba(255, 159, 64, 1)',
                    ],
                    borderWidth: 1,
                  },
                ],
              }}
            />
          )}
        </Paper>
      </Grid>
      <OutgoFormModal
        open={outgoModalOpen}
        onClose={() => setOutgoModalOpen(false)}
        onClickSubmit={onClickSubmitOutgo}
        id={selectedOutgo?.id || null}
        initialName={selectedOutgo ? selectedOutgo.name : ''}
        initialKind={selectedOutgo ? selectedOutgo.kind : 'other'}
        initialAmount={selectedOutgo ? selectedOutgo.amount : 0}
        initialRegisteredAt={
          selectedOutgo
            ? selectedOutgo.registered_at
            : calculateInitialRegisteredAt(year, month)
        }
      />
      <IncomeFormModal
        open={incomeModalOpen}
        onClose={() => setIncomeModalOpen(false)}
        onClickSubmit={onClickSubmitIncome}
        id={selectedIncome?.id || null}
        initialName={selectedIncome ? selectedIncome.name : ''}
        initialKind={selectedIncome ? selectedIncome.kind : 'other'}
        initialAmount={selectedIncome ? selectedIncome.amount : 0}
        initialRegisteredAt={
          selectedIncome
            ? selectedIncome.registered_at
            : calculateInitialRegisteredAt(year, month)
        }
      />
    </HeaderAndSidebar>
  );
};

export default MonthlyBalance;
